import { useContext, useState } from 'react';
import { Group, Stack, Text } from '@mantine/core';
import cx from 'clsx';
import { DragDropContext, Droppable, Draggable, DraggableLocation } from '@hello-pangea/dnd';
import { IconGripVertical } from '@tabler/icons-react';
import { Choice, Question, QuestionType } from '@generated/graphql';
import { TripDigestContext } from '@contexts/trip-digest-context';
import { useParams } from 'react-router-dom';
import { reorderChoices, reorderDnd } from '@lib/dnd-state';
import { useApolloClient } from '@apollo/client';
import { GET_QUESTIONS } from '@queries/questions';
import * as Sentry from '@sentry/react';
import QuestionThumbnail from './question-thumbnail';
import classes from './questionnaire-choices-dnd.module.css';

interface DndListHandleProps {
  question: Question;
}
function DndListHandle({ question }: DndListHandleProps) {
  const questionId = question.id.toString();
  const { tripProfileId } = useParams();
  const { questionStates, setQuestionState, removeQuestionState } = useContext(TripDigestContext)!;
  const rank = questionStates.get(questionId)?.rank as string[]; // TODO: fix types
  const [state, setState] = useState(reorderChoices(question.choices, rank));
  const apolloClient = useApolloClient();

  const updateContext = (currentState: Choice[]) => {
    setQuestionState({
      id: questionStates?.get(question.id)?.id || '',
      tripProfileId: tripProfileId || '',
      questionId: question.id,
      parentQuestionId: question.parentChoice?.questionId?.toString(),
      questionType: question.type,
      rank: [...currentState.map((item) => item.id)],
    });
  };

  interface UpdateHandlerParams {
    destination: DraggableLocation | null;
    source: DraggableLocation;
  }
  const updateHandler = ({ destination, source }: UpdateHandlerParams) => {
    const prevState = [...state];
    const reorderedChoices = reorderDnd({
      current: state,
      from: source.index,
      to: destination?.index || 0,
    }) as Choice[];
    setState(reorderedChoices);
    const newQuestionId = reorderedChoices[0].layerQuestionId as string;
    const oldQuestionId = prevState[0].layerQuestionId as string;
    // need to remove the child layer question and add new if the first choice changed
    if (question.type === QuestionType.LayerGroup && newQuestionId !== prevState[0].layerQuestionId) {
      console.log(`Adding child [${newQuestionId}]`);

      // TODO: use GET_QUESTION and still from cache?
      const questions = apolloClient.readQuery({ query: GET_QUESTIONS });
      const cachedQuestion = questions?.findQuestions.find((q) => q.id === newQuestionId) as Question;
      if (!cachedQuestion)
        Sentry.captureException(Error(`question [${newQuestionId}] not found if cache for appending rank to context.`));

      setQuestionState({
        // reuse the db id
        id: questionStates?.get(oldQuestionId)?.id || '',
        tripProfileId: tripProfileId || '',
        questionId: newQuestionId,
        parentQuestionId: question.id,
        questionType: QuestionType.Layer,
        rank: [...cachedQuestion.choices.map((item) => item.id)],
      });
      console.log(`Removing child [${oldQuestionId}]`);
      removeQuestionState(oldQuestionId);
    }
    updateContext(reorderedChoices);
  };

  const items = state.map((item, index) => (
    <Draggable key={item.id} index={index} draggableId={item.id.toString()} isDragDisabled={item.text.includes('TBD')}>
      {(provided, snapshot) => (
        <div
          className={cx(classes.item, {
            [classes.itemDragging]: snapshot.isDragging,
            [classes.itemTbd]: item.text.includes('TBD'),
          })}
          ref={provided.innerRef}
          {...provided.draggableProps}
        >
          <div {...provided.dragHandleProps} className={classes.dragHandle}>
            <IconGripVertical size="2.0rem" stroke={1.5} />
          </div>
          <div {...provided.dragHandleProps} className={classes.dragBox}>
            <Group justify="space-between">
              <Stack justify="flex-start">
                <Text>{item.text}</Text>
                <Text color="dimmed" size="sm">
                  {item.subtext}
                </Text>
              </Stack>
              {item.thumbnailFilename && <QuestionThumbnail item={item} />}
            </Group>
          </div>
        </div>
      )}
    </Draggable>
  ));

  return (
    <DragDropContext onDragEnd={updateHandler}>
      <Droppable droppableId="dnd-list" direction="vertical">
        {(provided) => (
          <div {...provided.droppableProps} ref={provided.innerRef}>
            {provided.placeholder}
            {items}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
}

export default DndListHandle;
